import Box, { BoxProps } from "@mui/material/Box";
import Checkbox, { CheckboxProps } from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup, { FormGroupProps } from "@mui/material/FormGroup";

import { Typography } from "../Typography/Typography";
import { Option } from "../types";

// Group mode: Multiple checkboxes with array values
interface GroupModeProps<T> extends Omit<BoxProps, "onChange" | "value"> {
  label?: string;
  value?: T[];
  onChange: (value: T[]) => void;
  options: Option<T>[];
  formGroupProps?: FormGroupProps;
  checkboxProps?: CheckboxProps;
}

// Individual mode: Single checkbox with boolean value
interface IndividualModeProps extends Omit<BoxProps, "onChange" | "value"> {
  label: string;
  checked?: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  checkboxProps?: CheckboxProps;
}

type Props<T> = GroupModeProps<T> | IndividualModeProps;

// Type guard to check if props are for individual mode
function isIndividualMode<T>(props: Props<T>): props is IndividualModeProps {
  return "checked" in props && !("options" in props);
}

export const CheckboxButtons = <T,>(props: Props<T>) => {
  // Individual checkbox mode
  if (isIndividualMode(props)) {
    const {
      label,
      checked = false,
      onChange,
      disabled,
      checkboxProps,
      ...rest
    } = props;

    return (
      <Box {...rest}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => !disabled && onChange(e.target.checked)}
              disabled={disabled}
              {...checkboxProps}
            />
          }
          label={label}
          disabled={disabled}
        />
      </Box>
    );
  }

  // Group mode (original behavior)
  const {
    label,
    value = [],
    options,
    onChange,
    formGroupProps,
    checkboxProps,
    ...rest
  } = props;

  const handleChange = (optionValue: T, disabled?: boolean) => {
    if (disabled) {
      return;
    }

    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue);
  };

  return (
    <Box {...rest}>
      {label && <Typography variant="titleSmall">{label}</Typography>}
      <FormGroup {...formGroupProps}>
        {options.map((option) => (
          <FormControlLabel
            key={`${option.value}`}
            control={
              <Checkbox
                checked={value.includes(option.value)}
                onChange={() => handleChange(option.value, option.disabled)}
                {...checkboxProps}
              />
            }
            label={option.label}
            disabled={option.disabled}
          />
        ))}
      </FormGroup>
    </Box>
  );
};
